import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useWebSocket from './useWebSocket';

const useDashboardWebSocket = () => {
  const { user } = useAuth();
  const userId = user?.id; // Only depend on user.id, not entire user object
  const [dashboardData, setDashboardData] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const hasSubscribedRef = useRef(false); // Track if we've already subscribed for this user
  
  const {
    isConnected,
    subscribeToDashboard,
    unsubscribeFromDashboard,
    error: wsError
  } = useWebSocket();

  // Handle dashboard updates from WebSocket
  const handleDashboardUpdate = useCallback((updateData) => {
    console.log('📊 Dashboard update received:', updateData);
    setDashboardData(prevData => ({
      ...prevData,
      ...updateData,
      lastUpdated: new Date().toISOString()
    }));
  }, []);

  // Subscribe to dashboard updates when connected - only once per userId
  useEffect(() => {
    if (isConnected && userId && !hasSubscribedRef.current) {
      // Add a small delay to ensure WebSocket is fully ready
      const timeoutId = setTimeout(() => {
        subscribeToDashboard();
        setIsSubscribed(true);
        hasSubscribedRef.current = true;
        
        // Set up global handler for dashboard updates
        window.dashboardUpdateHandler = handleDashboardUpdate;
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (hasSubscribedRef.current) {
        unsubscribeFromDashboard();
        setIsSubscribed(false);
        hasSubscribedRef.current = false;
        window.dashboardUpdateHandler = null;
      }
    };
  }, [isConnected, userId, subscribeToDashboard, unsubscribeFromDashboard, handleDashboardUpdate]);
  
  // Reset subscription flag when userId changes
  useEffect(() => {
    hasSubscribedRef.current = false;
    setIsSubscribed(false);
  }, [userId]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      window.dashboardUpdateHandler = null;
    };
  }, []);

  return {
    dashboardData,
    isConnected,
    isSubscribed,
    error: wsError,
    refreshDashboard: () => {
      // Trigger a manual refresh if needed
      if (window.dashboardRefreshHandler) {
        window.dashboardRefreshHandler();
      }
    }
  };
};

export default useDashboardWebSocket;
